import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../../../utils/apiClient.util";
import { Api_Url } from "../../../utils/ApiUrl";
import { ROUNDS } from "./staticData";
import {
  SortIcon,
  ExternalLinkIcon,
  CopyIcon,
  ArrowLeft,
  ArrowRight,
} from "./RoundIcons";

const LIMIT = 10;
const ORDER_FIELD = "transactionTime";

// Streaming CSV export endpoint (returns a JSON array of all matching transactions)
const EXPORT_CSV_URL = `${Api_Url}/users/bulk/special/transactions`;

const CHAIN = parseInt(process.env.REACT_APP_CHAIN_ID, 10);
const chainNetwork =
  CHAIN == 1 ? "mainnet" : CHAIN == 11155111 ? "testnet" : null;
const txExplorerUrl = (hash) =>
  chainNetwork == "testnet"
    ? `https://sepolia.etherscan.io/tx/${hash}`
    : `https://etherscan.io/tx/${hash}`;

const truncate = (str) => (str ? `${str.slice(0, 6)}...${str.slice(-4)}` : "-");

const fmtDate = (iso) => {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const fmtTime = (iso) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const fmt = (n, dec = 2) =>
  n != null ? Number(n).toLocaleString("en-US", { maximumFractionDigits: dec }) : "-";

const getPageNumbers = (current, total) => {
  if (total <= 6) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
  if (current >= total - 3)
    return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "...", current - 1, current, current + 1, "...", total];
};

const buildCSV = (rows, showCreditsUnlocked, saleLabel) => {
  const headers = [
    "Date",
    "Time",
    "Name",
    "Customer Email",
    "Wallet Address",
    "Hash",
    "Token",
    "Price",
    "Paid",
    "KAI Amount",
    "Payment",
    ...(showCreditsUnlocked ? ["Credits Unlocked"] : []),
    "Private Sale",
  ];
  const escape = (v) => {
    const s = String(v ?? "");
    return s.includes(",") || s.includes('"') || s.includes("\n")
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  return [
    headers.join(","),
    ...rows.map((r) =>
      [
        fmtDate(r.transactionTime),
        fmtTime(r.transactionTime) || "",
        r.name || "",
        r.email || "",
        r.by || "",
        r.transactionHash || "",
        r.tokenSelected || "",
        r.price ?? "",
        r.totalSaleConverted ?? "",
        r.tokenPurchased ?? "",
        `${r.amountInvested ?? ""} ${r.tokenSelected ?? ""}`.trim(),
        ...(showCreditsUnlocked ? [r.creditsUnlocked ?? ""] : []),
        saleLabel || r.privateSale || "",
      ]
        .map(escape)
        .join(","),
    ),
  ].join("\n");
};

const downloadBlob = (content, filename) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const RoundTransactions = ({ startDate, endDate, roundMeta = ROUNDS[0] }) => {
  const showCreditsUnlocked = roundMeta.showCreditsUnlocked;
  const privateSale = roundMeta.privateSale;
  // display name for the round — falls back to the raw API tag
  const saleLabel = roundMeta.saleLabel;

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // transactionTime sort: -1 = newest first (default), 1 = oldest first
  const [orderDirection, setOrderDirection] = useState(-1);
  const [exporting, setExporting] = useState(false);

  const fetchTransactions = useCallback(
    async (page) => {
      setLoading(true);
      try {
        const params = {
          privateSale,
          limit: LIMIT,
          offset: page,
          orderField: ORDER_FIELD,
          orderDirection,
        };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        const res = await apiClient.get("/users/special/transactions", {
          params,
        });
        const data = res.data.data.data;
        setRows(data.transactions || []);
        setTotalCount(data.count || 0);
        setTotalPages(data.pages || 1);
      } catch (e) {
        console.error("Failed to fetch special round transactions", e);
        // clear rather than leave the previous round's rows on screen
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [startDate, endDate, privateSale, orderDirection],
  );

  // reset to first page whenever the filters, round or sort order change
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, privateSale, orderDirection]);

  useEffect(() => {
    fetchTransactions(currentPage);
  }, [fetchTransactions, currentPage]);

  const toggleDateSort = () => {
    setOrderDirection((prev) => (prev === -1 ? 1 : -1));
  };

  const handlePageChange = (page) => {
    if (page === "..." || page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleExportCSV = async () => {
    if (!EXPORT_CSV_URL) {
      console.warn("Special round export CSV URL is not configured yet");
      return;
    }
    setExporting(true);
    try {
      const token = localStorage.getItem("accessToken");
      const qs = new URLSearchParams({
        privateSale,
        orderField: ORDER_FIELD,
        orderDirection: String(orderDirection),
      });
      if (startDate) qs.append("startDate", startDate);
      if (endDate) qs.append("endDate", endDate);

      const response = await fetch(`${EXPORT_CSV_URL}?${qs.toString()}`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!response.ok) throw new Error("Export failed");

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let raw = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        raw += decoder.decode(value, { stream: true });
      }
      raw += decoder.decode();

      const data = JSON.parse(raw);
      downloadBlob(
        buildCSV(data, showCreditsUnlocked, saleLabel),
        `${privateSale.toLowerCase()}-transactions.csv`,
      );
    } catch (e) {
      console.error("Export failed", e);
    } finally {
      setExporting(false);
    }
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);
  const startRow = totalCount === 0 ? 0 : (currentPage - 1) * LIMIT + 1;
  const endRow = Math.min(currentPage * LIMIT, totalCount);
  const copyToClipboard = (value) => {
    if (navigator?.clipboard) navigator.clipboard.writeText(value);
  };

  const colSpan = showCreditsUnlocked ? 10 : 9;

  return (
    <section className="roundlistmain">
      <div className="listtop">
        <div className="listheadings">
          <h1 className="listhead">Round Transactions</h1>
        </div>
        <button
          className="exportbtn"
          onClick={handleExportCSV}
          disabled={exporting}
        >
          {exporting ? "Exporting..." : "Export CSV"}
        </button>
      </div>

      <div className="listtable">
        <div className="maintable">
          <table>
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="flexmain sortbtn"
                    onClick={toggleDateSort}
                    title={
                      orderDirection === -1
                        ? "Sorted newest first — click for oldest first"
                        : "Sorted oldest first — click for newest first"
                    }
                  >
                    Date
                    <SortIcon direction={orderDirection} />
                  </button>
                </th>
                <th>Name</th>
                <th>Customer Email</th>
                <th>Wallet Address</th>
                <th>Hash</th>
                <th>Token</th>
                <th>Price</th>
                <th>Paid</th>
                <th>KAI Amount</th>
                <th>Payment</th>
                {showCreditsUnlocked && <th>Credits Unlocked</th>}
                <th>Private Sale</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: "30px", color: "#77868B" }}
                  >
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: "30px", color: "#77868B" }}
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={row._id || i}>
                    <td>
                      <div className="datetime">
                        {fmtDate(row.transactionTime)}
                        {fmtTime(row.transactionTime) && (
                          <span className="dull">{fmtTime(row.transactionTime)}</span>
                        )}
                      </div>
                    </td>
                    <td>{row.name || "-"}</td>
                    <td>{row.email || "-"}</td>
                    <td>
                      <div className="forflex">
                        {truncate(row.by)}
                        {row.by && (
                          <button
                            className="iconbtn"
                            type="button"
                            onClick={() => copyToClipboard(row.by)}
                          >
                            <CopyIcon />
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="forflex">
                        {truncate(row.transactionHash)}
                        {row.transactionHash && (
                          <a
                            className="iconbtn"
                            href={txExplorerUrl(row.transactionHash)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <ExternalLinkIcon />
                          </a>
                        )}
                      </div>
                    </td>
                    <td>{row.tokenSelected || "-"}</td>
                    <td>{row.price != null ? `$${fmt(row.price, 6)}` : "-"}</td>
                    <td>
                      {row.totalSaleConverted != null
                        ? `$${fmt(row.totalSaleConverted)}`
                        : "-"}
                    </td>
                    <td>{fmt(row.tokenPurchased)}</td>
                    <td>
                      {row.amountInvested != null
                        ? `${fmt(row.amountInvested, 6)} ${row.tokenSelected || ""}`
                        : "-"}
                    </td>
                    {showCreditsUnlocked && <td>{fmt(row.creditsUnlocked)}</td>}
                    <td>{saleLabel || row.privateSale || "-"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="footer-content newfootercontent">
          <div className="left-f">
            <h6>
              {totalCount > 0
                ? `SHOWING ${startRow}-${endRow} OF ${totalCount}`
                : "NO DATA"}
            </h6>
          </div>
          <div className="right-f">
            <nav aria-label="Page navigation">
              <ul className="pagination">
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#prev"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }}
                  >
                    <ArrowLeft />
                  </a>
                </li>
                {pageNumbers.map((page, i) => (
                  <li key={`${page}-${i}`} className="page-item">
                    <a
                      className={`page-link${page === currentPage ? " active" : ""}`}
                      href={`#page-${page}`}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(page);
                      }}
                    >
                      {page}
                    </a>
                  </li>
                ))}
                <li className="page-item">
                  <a
                    className="page-link"
                    href="#next"
                    onClick={(e) => {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }}
                  >
                    <ArrowRight />
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RoundTransactions;
